import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { FilePenLine, Loader2, RotateCcw } from "lucide-react";

// Editor de la plantilla por defecto (mensaje + firma) por tipo de documento.
// docType: "invoice" | "quotation". onSaved(resolved) actualiza el diálogo de envío.
export const EmailTemplateEditor = ({ docType, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ message: "", signature: "" });
  const [meta, setMeta] = useState({ can_edit_global: false, builtin: null });
  const [asGlobal, setAsGlobal] = useState(false);
  const [saving, setSaving] = useState(false);

  const openEditor = async () => {
    try {
      const r = await api.get(`/email-templates/${docType}`);
      const src = r.data.user || r.data.global || r.data.builtin || { message: "", signature: "" };
      setData({ message: src.message || "", signature: src.signature || "" });
      setMeta({ can_edit_global: r.data.can_edit_global, builtin: r.data.builtin });
      setAsGlobal(false);
      setOpen(true);
    } catch {
      toast.error("No se pudo cargar la plantilla");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/email-templates/${docType}`, {
        scope: asGlobal ? "global" : "user",
        message: data.message,
        signature: data.signature,
      });
      toast.success(asGlobal ? "Plantilla de la empresa guardada" : "Tu plantilla por defecto se guardó");
      setOpen(false);
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const restore = () => {
    if (meta.builtin) setData({ message: meta.builtin.message || "", signature: meta.builtin.signature || "" });
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 gap-1.5 px-2 text-xs text-blue-600"
        onClick={openEditor}
        data-testid={`edit-email-template-${docType}`}
      >
        <FilePenLine className="h-3.5 w-3.5" /> Editar plantilla por defecto
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid={`email-template-dialog-${docType}`}>
          <DialogHeader>
            <DialogTitle className="font-heading">
              Plantilla por defecto — {docType === "invoice" ? "Facturas" : "Cotizaciones"}
            </DialogTitle>
            <DialogDescription>
              Este mensaje y firma se cargarán automáticamente cada vez que envíes un correo. Podrás editarlos también antes de cada envío.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Mensaje por defecto</Label>
              <Textarea
                className="rounded-sm"
                rows={4}
                value={data.message}
                onChange={(e) => setData({ ...data, message: e.target.value })}
                placeholder="Mensaje que verá el cliente..."
                data-testid={`template-message-${docType}`}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Firma por defecto</Label>
              <Textarea
                className="rounded-sm"
                rows={4}
                value={data.signature}
                onChange={(e) => setData({ ...data, signature: e.target.value })}
                placeholder="Tu nombre, cargo, teléfono..."
                data-testid={`template-signature-${docType}`}
              />
            </div>
            {meta.can_edit_global && (
              <div className="flex items-center justify-between rounded-sm border border-border p-2.5">
                <div>
                  <span className="text-xs font-medium">Guardar como plantilla de la empresa</span>
                  <p className="text-[11px] text-muted-foreground">Aplica a todos los usuarios que no tengan la suya.</p>
                </div>
                <Switch checked={asGlobal} onCheckedChange={setAsGlobal} data-testid={`template-as-global-${docType}`} />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" size="sm" className="rounded-sm gap-1.5 mr-auto" onClick={restore} data-testid={`template-restore-${docType}`}>
              <RotateCcw className="h-3.5 w-3.5" /> Restaurar base
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm font-bold gap-2" onClick={save} disabled={saving} data-testid={`template-save-${docType}`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Guardar plantilla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
